import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, User, Calendar, Filter } from 'lucide-react';
import api from '../services/api';
import { productsService } from '../services/products.service';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { clsx } from 'clsx';

interface AuditLog {
  id: number;
  action: string;
  entityType: string;
  entityId: number | null;
  content?: string;
  createdAt: string;
  ipAddress: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

const actionColors: Record<string, string> = {
  CREATE: 'bg-subtle text-accent',
  UPDATE: 'bg-subtle text-warning',
  DELETE: 'bg-subtle text-danger',
  LOGIN: 'bg-subtle text-ink-primary',
  LOGOUT: 'bg-subtle text-ink-secondary',
};

const actionLabels: Record<string, string> = {
  CREATE: 'Criar',
  UPDATE: 'Atualizar',
  DELETE: 'Excluir',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
};

const ENTITY_TYPES = [
  { value: 'user', label: 'Usuário' },
  { value: 'lead', label: 'Lead' },
  { value: 'product', label: 'Produto' },
  { value: 'stage', label: 'Etapa' },
  { value: 'origin', label: 'Origem' },
];

export function AuditLogPage() {
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    productId: '',
    startDate: '',
    endDate: '',
    page: 1,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.list(),
  });
  const products: any[] = productsData?.data || productsData || [];

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(filters.page));
      params.append('limit', '20');
      if (filters.action) {
        params.append('action', filters.action);
      }
      if (filters.entityType) {
        params.append('entityType', filters.entityType);
      }
      if (filters.productId) {
        params.append('productId', filters.productId);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const response = await api.get(`/audit/logs?${params}`);
      return response.data;
    },
  });

  const logs: AuditLog[] = data?.logs || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink-primary flex items-center gap-2">
          <FileText className="w-6 h-6 text-ink-primary" />
          Logs de Auditoria
        </h1>
        <p className="text-sm text-ink-tertiary mt-1">Histórico de ações realizadas no sistema</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="w-5 h-5 text-ink-tertiary flex-shrink-0" />

          {/* Action filter */}
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
            className="input w-auto"
          >
            <option value="">Todas as ações</option>
            {Object.entries(actionLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          {/* Entity type filter */}
          <select
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value, page: 1 })}
            className="input w-auto"
          >
            <option value="">Todos os tipos</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          {/* Product filter */}
          {products.length > 0 && (
            <select
              value={filters.productId}
              onChange={(e) => setFilters({ ...filters, productId: e.target.value, page: 1 })}
              className="input w-auto"
            >
              <option value="">Todos os produtos</option>
              {products.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          {/* Date range */}
          <div>
            <label className="block text-xs font-medium text-ink-tertiary mb-1">Data início</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
              className="border border-border-strong rounded-[10px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-tertiary mb-1">Data fim</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
              className="border border-border-strong rounded-[10px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent"
            />
          </div>
        </div>
      </div>

      {/* Logs list */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : logs.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="w-12 h-12 text-ink-tertiary mx-auto mb-4" />
          <p className="text-ink-secondary">Nenhum log encontrado</p>
        </div>
      ) : (
        <div className="card divide-y divide-border">
          {logs.map((log: AuditLog) => (
            <div key={log.id} className="py-4 first:pt-0 last:pb-0">
              <div className="flex items-start gap-4">
                <div
                  className={clsx(
                    'px-2 py-1 rounded text-xs font-medium flex-shrink-0',
                    actionColors[log.action] || 'bg-subtle text-ink-secondary'
                  )}
                >
                  {actionLabels[log.action] || log.action}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-ink-primary">{log.content}</p>
                  <div className="flex items-center gap-4 mt-1 text-sm text-ink-tertiary flex-wrap">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {log.user.name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </span>
                    {log.entityType && (
                      <span className="text-xs bg-subtle text-ink-secondary px-2 py-0.5 rounded">
                        {log.entityType}
                      </span>
                    )}
                    {log.ipAddress && (
                      <span className="text-ink-tertiary text-xs">{log.ipAddress}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={filters.page === 1}
            className="btn-secondary"
          >
            Anterior
          </button>
          <span className="text-ink-secondary">
            Página {filters.page} de {data.totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={filters.page === data.totalPages}
            className="btn-secondary"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default AuditLogPage;
